import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { memo } from 'react';

import { DraggableCell } from '@components';
import type { Column, ColumnLayout, DraggableItem, FormMetadata, PaginationProps } from '@types';
import { cn } from '@utils';

interface DraggableRowProps<T extends DraggableItem> extends FormMetadata {
  item: T;
  index: number;
  columns: Column[];
  columnLayouts: ColumnLayout[];
  isDraggable: boolean;
  actionHandlers?: any;
  pagination?: PaginationProps;
}

export const DraggableRow = memo(
  <T extends DraggableItem>({
    item,
    index,
    columns,
    columnLayouts,
    isDraggable,
    actionHandlers,
    endpoint,
    formType,
    pagination,
  }: DraggableRowProps<T>) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: item.id,
      disabled: !isDraggable,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 999 : 'auto',
    };

    return (
      <div
        {...attributes}
        {...listeners}
        ref={setNodeRef}
        style={style}
        className={cn(
          'flex flex-1',
          index % 2 === 0 && 'bg-white',
          index % 2 !== 0 && 'bg-gray-50',
          isDraggable ? 'cursor-grab active:cursor-grabbing touch-none select-none' : 'cursor-text',
        )}
      >
        {columns.map((column, colIndex) => (
          <DraggableCell
            key={`cell-${colIndex}`}
            colId={colIndex}
            rowId={index}
            column={column}
            endpoint={endpoint}
            pagination={pagination}
            item={item}
            layout={columnLayouts[colIndex]}
            actionHandlers={actionHandlers}
            formType={formType}
          />
        ))}
      </div>
    );
  },
);
